import type { ChangeEvent, ComponentProps, ReactNode } from "react"
import { Controller } from "react-hook-form"
import type { FieldPath, FieldValues } from "react-hook-form"
import useField from "./useField"

type RenderProps<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
> = Parameters<
  ComponentProps<typeof Controller<TValues, TName>>["render"]
>[number]

type Props<TValues extends FieldValues, TName extends FieldPath<TValues>> = {
  name: TName
  required?: boolean
  children: (
    props: RenderProps<TValues, TName> & {
      isError: boolean
      error: string | undefined
      field: RenderProps<TValues, TName>["field"]
    },
  ) => ReactNode
}

export default function FormControl<
  TValues extends FieldValues,
  TName extends FieldPath<TValues>,
>({ name, required, children }: Props<TValues, TName>) {
  const { control, error, isError } = useField<TValues>(name)

  return (
    <Controller
      name={name}
      control={control}
      rules={{ required }}
      render={({ field, ...restRenderProps }) => {
        const { value, onChange, ...restField } = field

        const nullSafeField = {
          ...restField,
          value: value ?? "",
          onChange: (e: unknown) => {
            if (
              e instanceof Event ||
              (typeof e === "object" && e !== null && "target" in e)
            ) {
              const val = (e as ChangeEvent<HTMLInputElement>).target.value
              onChange(val === "" ? null : val)
            } else {
              onChange(e === "" ? null : e)
            }
          },
        } as typeof field

        return (
          <>
            {children({
              field: nullSafeField,
              ...restRenderProps,
              isError,
              error,
            })}
          </>
        )
      }}
    />
  )
}
